import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Box, Component, Grid, Circle, Layers, Maximize, MousePointer2 } from 'lucide-react';

const IndustrialDesign: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Form Function Institute";
    
    return (
        <div className="min-h-screen bg-[#f5f5f5] font-sans text-[#111] selection:bg-[#ff4400] selection:text-white">
            {/* Sidebar Navigation */}
            <nav className="fixed left-0 top-0 h-full w-20 md:w-24 bg-white border-r border-[#e5e5e5] hidden lg:flex flex-col justify-between items-center py-10 z-50">
                <div className="w-10 h-10 bg-[#111] text-white flex items-center justify-center font-bold text-xl">
                    F
                </div>
                
                <div className="flex flex-col gap-12 text-[#999]">
                    <a href="#" className="hover:text-[#111] transition-colors"><Grid size={24} strokeWidth={1.5} /></a>
                    <a href="#" className="hover:text-[#111] transition-colors"><Layers size={24} strokeWidth={1.5} /></a>
                    <a href="#" className="hover:text-[#111] transition-colors"><Box size={24} strokeWidth={1.5} /></a>
                    <a href="#" className="hover:text-[#111] transition-colors"><Component size={24} strokeWidth={1.5} /></a>
                </div>

                <div className="text-[10px] font-bold uppercase tracking-widest rotate-180 writing-vertical-rl text-[#999]">
                    © 2026
                </div>
            </nav>

            {/* Mobile Nav Top */}
            <div className="lg:hidden fixed top-0 w-full bg-white border-b border-[#e5e5e5] p-4 flex justify-between items-center z-50">
                 <span className="font-bold">{schoolName}</span>
                 <Button variant="ghost" size="sm"><Grid size={20}/></Button>
            </div>

            {/* Main Content Area */}
            <div className="lg:pl-24">
                
                {/* Hero / Showcase */}
                <header className="min-h-screen flex flex-col justify-center px-8 md:px-24 py-20 relative overflow-hidden bg-[#f5f5f5]">
                    <div className="max-w-4xl relative z-10">
                        <span className="block font-mono text-xs text-[#666] mb-8">
                            Dieter Rams: "Good design is as little design as possible."
                        </span>
                        
                        <h1 className="text-6xl md:text-9xl font-bold tracking-tighter text-[#111] leading-none mb-12">
                            Less, <br/>
                            But Better.
                        </h1>
                        
                        <div className="flex flex-col md:flex-row gap-12 items-start max-w-2xl">
                            <p className="text-lg text-[#666] leading-relaxed">
                                We educate industrial designers who understand the responsibility of creating objects. Focus on sustainability, usability, and pure geometry.
                            </p>
                            
                            <div className="flex flex-col gap-4 w-full md:w-auto">
                                <Button className="rounded-none bg-[#111] hover:bg-[#ff4400] text-white font-mono text-xs h-12 px-8 w-full md:w-48 transition-colors">
                                    View Portfolio
                                </Button>
                                <Button variant="outline" className="rounded-none border-[#ccc] text-[#111] hover:bg-white font-mono text-xs h-12 px-8 w-full md:w-48 bg-transparent">
                                    Admissions
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Product Mockup Graphic */}
                    <div className="absolute right-[-10%] top-[20%] w-[60%] h-[70%] opacity-10 pointer-events-none hidden lg:block">
                        <img 
                            src="https://images.unsplash.com/photo-1596495394034-78be29d1082c?q=80&w=2070&auto=format&fit=crop" 
                            alt="Minimal Product" 
                            className="w-full h-full object-contain grayscale"
                        />
                    </div>
                </header>

                {/* Grid System Section */}
                <section className="border-t border-[#e5e5e5] bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#e5e5e5]">
                        {[
                            { title: "Product Design", num: "01", desc: "Consumer electronics and furniture." },
                            { title: "Material Science", num: "02", desc: "Sustainable polymers and metals." },
                            { title: "Prototyping", num: "03", desc: "3D printing and CNC machining." },
                        ].map((item, i) => (
                            <div key={i} className="p-16 hover:bg-[#fafafa] transition-colors group cursor-pointer h-[400px] flex flex-col justify-between">
                                <div className="flex justify-between items-start">
                                    <span className="font-mono text-xs text-[#999]">/{item.num}</span>
                                    <Maximize size={20} className="text-[#e5e5e5] group-hover:text-[#111] transition-colors" />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-bold tracking-tight mb-4">{item.title}</h3>
                                    <p className="text-[#666] text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Quote / Footer */}
                <footer className="bg-[#111] text-white py-24 px-8 md:px-24">
                     <div className="max-w-4xl">
                         <div className="mb-20">
                             <Circle size={12} className="fill-white mb-8" />
                             <h2 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight">
                                 Design is intelligence made visible. Join us to shape the objects of tomorrow.
                             </h2>
                         </div>
                         
                         <div className="grid grid-cols-2 md:grid-cols-4 gap-12 font-mono text-xs text-[#666]">
                             <div>
                                 <h4 className="text-white mb-4">Contact</h4>
                                 <p className="mb-2">Berlin, Germany</p>
                                 <p>hello@formfunction.edu</p>
                             </div>
                             <div>
                                 <h4 className="text-white mb-4">Social</h4>
                                 <p className="mb-2"><a href="#" className="hover:text-white">Instagram</a></p>
                                 <p><a href="#" className="hover:text-white">Behance</a></p>
                             </div>
                             <div>
                                 <h4 className="text-white mb-4">Legal</h4>
                                 <p className="mb-2"><a href="#" className="hover:text-white">Imprint</a></p>
                                 <p><a href="#" className="hover:text-white">Privacy</a></p>
                             </div>
                         </div>
                         
                         <div className="mt-20 pt-8 border-t border-[#333] font-mono text-[10px] text-[#444] uppercase tracking-widest flex justify-between">
                             <span>{schoolName}</span>
                             <span>Est. 1992</span>
                         </div>
                     </div>
                </footer>
            </div>
        </div>
    );
};

export default IndustrialDesign;
